import React, { createContext, useContext, useMemo } from "react";
import {
  WEBFRONT_RUNTIME_CONFIG,
  type DataEnvironment,
  type EndpointProfile,
} from "../api/runtimeConfig";

type DataEnvironmentContextType = {
  dataEnvironment: DataEnvironment;
  endpointProfiles: Record<DataEnvironment, EndpointProfile>;
  currentProfile: EndpointProfile;
  publishTargetEnvironment: "prod";
  publishTargetProfile: EndpointProfile;
};

const DataEnvironmentContext = createContext<DataEnvironmentContextType | undefined>(undefined);

export const DataEnvironmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dataEnvironment = WEBFRONT_RUNTIME_CONFIG.initialDataEnvironment;
  const endpointProfiles = WEBFRONT_RUNTIME_CONFIG.endpointProfiles;
  const publishTargetEnvironment = WEBFRONT_RUNTIME_CONFIG.publishTargetEnvironment;

  const contextValue = useMemo<DataEnvironmentContextType>(
    () => ({
      dataEnvironment,
      endpointProfiles,
      currentProfile: endpointProfiles[dataEnvironment],
      publishTargetEnvironment,
      publishTargetProfile: endpointProfiles[publishTargetEnvironment],
    }),
    [dataEnvironment, endpointProfiles, publishTargetEnvironment]
  );

  return (
    <DataEnvironmentContext.Provider value={contextValue}>
      {children}
    </DataEnvironmentContext.Provider>
  );
};

export const useDataEnvironment = () => {
  const context = useContext(DataEnvironmentContext);
  if (!context) {
    throw new Error("useDataEnvironment must be used within a DataEnvironmentProvider");
  }
  return context;
};
